import React from "react";
import ReactSlider from "react-slider";
import styled from "styled-components";
import { typeSize } from "../theme";

/*
 * The slider is 18px tall (the thumb) with a 6px track through the middle.
 * The target's label hangs below the mark, so the slider reserves room for
 * it rather than spilling into whatever comes next.
 */
const StyledSlider = styled(ReactSlider)`
  width: 100%;
  height: 18px;
  margin-bottom: 22px;
`;

const StyledMark = styled.div`
  top: 3px;
  width: 2px;
  height: 12px;
  margin-left: 8px;
  border-radius: 1px;
  background-color: ${(props) => props.theme.colors.grey};
  pointer-events: none;
`;

const StyledThumb = styled.div<{ disabled: boolean }>`
  height: 18px;
  width: 18px;
  border-radius: 50%;
  background-color: ${(props) =>
    props.disabled ? props.theme.colors.grey : props.theme.colors.primary};
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  cursor: ${(props) => (props.disabled ? "default" : "grab")};

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.grey0};
    outline-offset: 2px;
  }
`;

/** Right-aligned to the mark, so a target at the far end stays inside. */
const StyledMarkText = styled.div`
  position: absolute;
  top: 18px;
  right: -2px;
  white-space: nowrap;
  font-size: ${typeSize("caption")};
  color: ${(props) => props.theme.colors.textMuted ?? props.theme.colors.grey1};
`;

const Thumb = (props: any, disabled: boolean) => (
  <StyledThumb {...props} disabled={disabled} />
);

const Mark = (props: any, value: number) => (
  <StyledMark {...props}>
    <StyledMarkText>Target {value}</StyledMarkText>
  </StyledMark>
);

/** Index 0 is the filled part (left of the thumb), 1 the rest. */
const StyledTrack = styled.div<{ index: number; disabled: boolean }>`
  top: 6px;
  height: 6px;
  border-radius: 999px;
  background: ${(props) =>
    props.index === 0
      ? props.disabled
        ? props.theme.colors.grey
        : props.theme.colors.primary
      : props.theme.colors.grey4};
`;

// react-slider's track state has no "disabled", so it's passed in.
const Track = (props: any, state: any, disabled: boolean) => (
  <StyledTrack {...props} index={state.index} disabled={disabled} />
);

export const Slider = ({
  onChange,
  value,
  onAfterChange,
  mark,
  disabled,
  max,
  min,
}: {
  onChange: (val: number) => void;
  value: number;
  onAfterChange: () => void;
  mark: number;
  disabled: boolean;
  max: number;
  min: number;
}) => {
  return (
    <StyledSlider
      renderTrack={(props, state) => Track(props, state, disabled)}
      renderThumb={(val) => Thumb(val, disabled)}
      renderMark={(val: any) => Mark(val, mark)}
      value={value}
      onChange={(val) => onChange(+val)}
      onAfterChange={onAfterChange}
      min={min}
      marks={[mark]}
      disabled={disabled}
      max={max}
    />
  );
};
